import { ContractVisitGroup } from '../components/VisitGroupEditor'
import {
  getGroupAssignments,
  syncGroupServiceIds,
  VisitGroupServiceAssignment,
} from './visitGroupBoardOps'

export interface CombinationRule {
  category_id: string
  combination_group: string
  is_standalone: boolean
}

export interface ServiceForGrouping {
  id: string
  service_name?: string
  name?: string
  service_item_id?: string
  recurrence_days?: number[]
  service_type_id?: string
  category_id?: string
  dsny_integration?: boolean
  combine_with_dsny?: boolean
  combine_target?: 'dsny' | 'hallway' | null
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  // Recurrence override (custom + hallway DSNY rows)
  recurrence_type?: string
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  recurrence_day_of_month?: number | null
  /** Monthly day-of-month row that should ride the nearest weekly DSNY visit. */
  snap_to_closest?: boolean
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function buildRulesMap(rules: CombinationRule[]) {
  const rulesByCategory: Record<string, { combination_group: string; is_standalone: boolean }> = {}
  for (const r of rules) {
    rulesByCategory[r.category_id] = {
      combination_group: r.combination_group,
      is_standalone: r.is_standalone,
    }
  }
  return rulesByCategory
}

function getGroupKeyForService(
  s: ServiceForGrouping,
  rulesByCategory: Record<string, { combination_group: string; is_standalone: boolean }>
): { mergeKey: string; groupLabel: string } {
  if (s.combine_target === 'dsny') {
    return { mergeKey: 'dsny_exterior', groupLabel: 'DSNY Exterior' }
  }
  if (s.combine_target === 'hallway') {
    return { mergeKey: 'hallway_cleaning', groupLabel: 'Hallway Cleaning' }
  }
  if (s.combine_target === null) {
    return {
      mergeKey: `standalone_${s.id}`,
      groupLabel: s.service_name || s.name || 'Service',
    }
  }
  const catId = s.service_type_id || s.category_id || ''
  const rule = catId && !catId.startsWith('standalone_') ? rulesByCategory[catId] : undefined

  if (!rule || rule.is_standalone || catId.startsWith('standalone_')) {
    return {
      mergeKey: `standalone_${s.id}`,
      groupLabel: s.service_name || s.name || 'Service',
    }
  }
  return {
    mergeKey: rule.combination_group,
    groupLabel: rule.combination_group,
  }
}

/**
 * Day-centric visit group suggestion: services that share a combination group AND
 * the same calendar day merge into one visit. Groups with identical service membership
 * across different days are consolidated (union of recurrence_days).
 */
function primaryAssignments(serviceIds: string[]): VisitGroupServiceAssignment[] {
  return serviceIds.map((service_id) => ({ service_id, schedule_mode: 'primary' as const }))
}

function monthlyLabel(s: ServiceForGrouping): string {
  if (s.recurrence_weekday != null && s.recurrence_occurrence != null) {
    const occ = ['1st', '2nd', '3rd', '4th', 'Last'][Math.min(s.recurrence_occurrence, 5) - 1] || '1st'
    return `${occ} ${DAY_LABELS[s.recurrence_weekday]} monthly`
  }
  if (s.recurrence_day_of_month != null) {
    return `Day ${s.recurrence_day_of_month} monthly`
  }
  return 'Monthly'
}

function recurrenceLabel(s: ServiceForGrouping): string {
  const rtype = s.recurrence_type || 'weekly'
  if (rtype === 'daily') return 'Daily'
  if (rtype === 'monthly') return monthlyLabel(s)
  const days = (s.recurrence_days || []).slice().sort((a, b) => a - b)
  const dayLabel = days.length > 0 ? days.map((d) => DAY_LABELS[d]).join('/') : 'Unscheduled'
  return rtype === 'bi_weekly' ? `${dayLabel} bi-weekly` : dayLabel
}

export function suggestVisitGroupsDayCentric(
  services: ServiceForGrouping[],
  rules: CombinationRule[],
  contractId: string,
  contractStartDate?: string | null,
  contractEndDate?: string | null
): ContractVisitGroup[] {
  if (!services || services.length === 0) return []

  const rulesByCategory = buildRulesMap(rules)

  // Partition by recurrence semantics.
  // Non-weekly rows that combine with DSNY/hallway piggyback onto the weekly group
  // (monthly nth-weekday, day-of-month, bi-weekly combine, snap-to-closest).
  const weeklyServices: ServiceForGrouping[] = []
  const standaloneServices: ServiceForGrouping[] = []
  const snapServices: ServiceForGrouping[] = []
  for (const s of services) {
    const rtype = s.recurrence_type || 'weekly'
    const combines =
      s.combine_target === 'dsny' ||
      s.combine_target === 'hallway' ||
      !!s.combine_with_dsny
    if (rtype !== 'weekly' && combines) {
      snapServices.push(s)
    } else if (s.snap_to_closest) {
      snapServices.push(s)
    } else if (rtype === 'weekly') {
      weeklyServices.push(s)
    } else {
      standaloneServices.push(s)
    }
  }

  const baseId = Date.now()
  const out: ContractVisitGroup[] = []
  let idx = 0

  // ---- Weekly services: day-centric bucketing (existing behavior) ----
  const dayBuckets: Record<
    string,
    {
      mergeKey: string
      groupLabel: string
      day: number
      serviceIds: Set<string>
      dsny: boolean
      assignedCrewId: string | null
      assignedEmployeeId: string | null
    }
  > = {}
  for (const s of weeklyServices) {
    const days = (s.recurrence_days || []).slice().sort((a, b) => a - b)
    if (days.length === 0) continue
    const { mergeKey, groupLabel } = getGroupKeyForService(s, rulesByCategory)
    for (const day of days) {
      const bucketKey = `${mergeKey}::${day}`
      if (!dayBuckets[bucketKey]) {
        dayBuckets[bucketKey] = {
          mergeKey,
          groupLabel,
          day,
          serviceIds: new Set(),
          dsny: false,
          assignedCrewId: null,
          assignedEmployeeId: null,
        }
      }
      dayBuckets[bucketKey].serviceIds.add(s.id)
      if (s.dsny_integration) dayBuckets[bucketKey].dsny = true
      dayBuckets[bucketKey].assignedCrewId ||= s.assigned_crew_id || null
      dayBuckets[bucketKey].assignedEmployeeId ||= s.assigned_employee_id || null
    }
  }

  const bySignature: Record<
    string,
    {
      serviceIds: string[]
      days: number[]
      groupLabel: string
      dsny: boolean
      assignedCrewId: string | null
      assignedEmployeeId: string | null
    }
  > = {}
  Object.values(dayBuckets).forEach((bucket) => {
    const serviceIds = Array.from(bucket.serviceIds).sort()
    const signature = serviceIds.join('|')
    if (!bySignature[signature]) {
      bySignature[signature] = {
        serviceIds,
        days: [],
        groupLabel: bucket.groupLabel,
        dsny: bucket.dsny,
        assignedCrewId: bucket.assignedCrewId,
        assignedEmployeeId: bucket.assignedEmployeeId,
      }
    }
    if (!bySignature[signature].days.includes(bucket.day)) bySignature[signature].days.push(bucket.day)
    if (bucket.dsny) bySignature[signature].dsny = true
  })

  Object.values(bySignature).forEach((group) => {
    idx++
    const sortedDays = group.days.sort((a, b) => a - b)
    const dayLabel = sortedDays.length > 0 ? sortedDays.map((d) => DAY_LABELS[d]).join('/') : 'Unscheduled'
    const serviceNames = group.serviceIds
      .map((id) => services.find((s) => s.id === id))
      .filter(Boolean)
      .map((s) => s!.service_name || s!.name || 'Service')
    const name =
      (group.dsny || group.groupLabel === 'Hallway Cleaning') && serviceNames.length >= 1
        ? `${group.groupLabel} (${dayLabel})`
        : serviceNames.length === 1
          ? `${serviceNames[0]} (${dayLabel})`
          : `${group.groupLabel} (${dayLabel})`

    out.push({
      id: `temp-suggest-${baseId}-${idx}`,
      contract_id: contractId,
      name,
      recurrence_type: 'weekly',
      recurrence_days: sortedDays,
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_day_of_month: null,
      start_date: contractStartDate || null,
      end_date: contractEndDate || null,
      scheduled_start_time: null,
      scheduled_end_time: null,
      billing_driver: null,
      dsny_integration: group.dsny,
      dsny_pickup_days: [],
      is_active: true,
      assigned_crew_id: group.assignedCrewId,
      assigned_employee_id: group.assignedEmployeeId,
      service_ids: group.serviceIds,
      service_assignments: primaryAssignments(group.serviceIds),
    })
  })

  // ---- Standalone non-weekly services: one group each with its own recurrence ----
  for (const s of standaloneServices) {
    idx++
    const rtype = s.recurrence_type || 'weekly'
    const days = (s.recurrence_days || []).slice().sort((a, b) => a - b)
    const serviceName = s.service_name || s.name || 'Service'
    out.push({
      id: `temp-suggest-${baseId}-${idx}`,
      contract_id: contractId,
      name: `${serviceName} (${recurrenceLabel(s)})`,
      recurrence_type: rtype,
      recurrence_days: rtype === 'monthly' || rtype === 'daily' ? [] : days,
      recurrence_weekday: rtype === 'monthly' ? s.recurrence_weekday ?? null : null,
      recurrence_occurrence: rtype === 'monthly' ? s.recurrence_occurrence ?? null : null,
      recurrence_day_of_month: rtype === 'monthly' ? s.recurrence_day_of_month ?? null : null,
      start_date: contractStartDate || null,
      end_date: contractEndDate || null,
      scheduled_start_time: null,
      scheduled_end_time: null,
      billing_driver: null,
      dsny_integration: false,
      dsny_pickup_days: [],
      is_active: true,
      assigned_crew_id: s.assigned_crew_id || null,
      assigned_employee_id: s.assigned_employee_id || null,
      service_ids: [s.id],
      service_assignments: primaryAssignments([s.id]),
    })
  }

  // ---- Piggyback / snap services: attach onto weekly DSNY or hallway group ----
  for (const s of snapServices) {
    const wantsHallway = s.combine_target === 'hallway'
    const wantsDsny =
      s.combine_target === 'dsny' || (!wantsHallway && (!!s.combine_with_dsny || !!s.dsny_integration))
    const target =
      (wantsHallway
        ? out.find((g) => /^Hallway Cleaning\b/i.test(g.name || ''))
        : wantsDsny
          ? out.find((g) => g.dsny_integration)
          : undefined) ||
      out.find((g) => g.dsny_integration) ||
      out.find((g) => /^Hallway Cleaning\b/i.test(g.name || '')) ||
      out.find((g) => g.recurrence_type === 'weekly')

    const rtype = s.recurrence_type || 'monthly'
    const assignment: VisitGroupServiceAssignment = {
      service_id: s.id,
      schedule_mode: 'piggyback',
      recurrence_type: rtype,
      recurrence_day_of_month: s.recurrence_day_of_month ?? null,
      recurrence_weekday: s.recurrence_weekday ?? null,
      recurrence_occurrence: s.recurrence_occurrence ?? null,
      recurrence_nearest_visit: !!s.snap_to_closest,
    }
    if (target) {
      const existing = getGroupAssignments(target)
      Object.assign(
        target,
        syncGroupServiceIds({
          ...target,
          assigned_crew_id: s.assigned_crew_id || target.assigned_crew_id || null,
          assigned_employee_id:
            s.assigned_employee_id || target.assigned_employee_id || null,
          service_assignments: [...existing, assignment],
        })
      )
    } else {
      // No weekly group to ride; fall back to its own non-weekly group.
      idx++
      const serviceName = s.service_name || s.name || 'Service'
      out.push({
        id: `temp-suggest-${baseId}-${idx}`,
        contract_id: contractId,
        name: `${serviceName} (${recurrenceLabel(s)})`,
        recurrence_type: rtype,
        recurrence_days: rtype === 'monthly' || rtype === 'daily' ? [] : (s.recurrence_days || []),
        recurrence_weekday: rtype === 'monthly' ? s.recurrence_weekday ?? null : null,
        recurrence_occurrence: rtype === 'monthly' ? s.recurrence_occurrence ?? null : null,
        recurrence_day_of_month: rtype === 'monthly' ? s.recurrence_day_of_month ?? null : null,
        start_date: contractStartDate || null,
        end_date: contractEndDate || null,
        scheduled_start_time: null,
        scheduled_end_time: null,
        billing_driver: null,
        dsny_integration: false,
        dsny_pickup_days: [],
        is_active: true,
        assigned_crew_id: s.assigned_crew_id || null,
        assigned_employee_id: s.assigned_employee_id || null,
        service_ids: [s.id],
        service_assignments: primaryAssignments([s.id]),
      })
    }
  }

  return out
}

/** Returns true when two services can't share a plain (primary) merge. */
export function servicesHaveIncompatibleSchedules(
  a: ServiceForGrouping,
  b: ServiceForGrouping
): boolean {
  const typeA = a.recurrence_type || 'weekly'
  const typeB = b.recurrence_type || 'weekly'
  // Different cadences (e.g. monthly vs weekly) can't merge as primaries — monthly
  // rows carry no weekday set, so the day-based check below would miss them.
  if (typeA !== typeB) return true
  if (a.snap_to_closest || b.snap_to_closest) return true
  const daysA = new Set(a.recurrence_days || [])
  const daysB = new Set(b.recurrence_days || [])
  if (daysA.size === 0 || daysB.size === 0) return false
  const intersection = Array.from(daysA).filter((d) => daysB.has(d))
  // Incompatible if they share no days but both have schedules
  return intersection.length === 0
}
